import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../App";
import { Search, Shield, AlertTriangle } from "lucide-react";

export default function AdminFailedSignups() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [ipFilter, setIpFilter] = useState("");

  const fetchList = useCallback(() => {
    setData(null);
    const params = { page, limit: 100 };
    if (ipFilter.trim()) params.ip_filter = ipFilter.trim();
    apiClient
      .get("/admin/failed-signups", { params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Failed to load signup attempts"));
  }, [page, ipFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const ready = typeof data === "object" && data !== null;

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="admin-failed-signups">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Signup Attempts</h1>
        <p className="text-sm text-slate-500 mt-1">
          Recent signup attempts (rolling 24-hour window, auto-expires via TTL).
        </p>
      </header>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Filter by IP address"
            value={ipFilter}
            onChange={(e) => setIpFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); fetchList(); } }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-signups-ip-filter"
          />
        </div>
        <button
          onClick={() => { setPage(1); fetchList(); }}
          className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm"
          data-testid="admin-signups-apply"
        >
          Apply
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm mb-4">{error}</div>
      )}

      {/* Top IPs */}
      {ready && Array.isArray(data.top_ips) && data.top_ips.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-slate-400" />
            Top IPs by Attempt Count (24h)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.top_ips.map((ip) => (
              <div
                key={ip.ip_address}
                className={`bg-white border rounded-xl p-4 ${ip.count >= 3 ? "border-red-200 bg-red-50/30" : "border-slate-200"}`}
                data-testid={`admin-signups-top-ip-${ip.ip_address}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-slate-800">{ip.ip_address}</span>
                  <span className={`text-sm font-bold ${ip.count >= 3 ? "text-red-600" : "text-slate-700"}`}>
                    {ip.count >= 3 && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    {ip.count} attempts
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Last: {fmtTS(ip.last_attempt)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Attempts table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {!ready ? (
          <div className="p-4" data-testid="admin-signups-loading">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-slate-100 rounded animate-pulse my-2 mx-2" />
            ))}
          </div>
        ) : data.total === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="admin-signups-empty">
            <Shield className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            No signup attempts in the last 24 hours.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-signups-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">IP Address</th>
                  <th className="px-4 py-3 font-medium">Route</th>
                  <th className="px-4 py-3 font-medium">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={`${e.ip_address}-${e.created_at}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-400 text-xs">{(page - 1) * 100 + i + 1}</td>
                    <td className="px-4 py-3 font-mono text-slate-800">{e.ip_address || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.route || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtTS(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {ready && data.total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
          <span>{data.total} attempts · page {data.page}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40" data-testid="admin-signups-prev-page">Previous</button>
            <button disabled={page * data.limit >= data.total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40" data-testid="admin-signups-next-page">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtTS(v) {
  if (typeof v !== "string" || v.length < 10) return "—";
  try { return new Date(v).toLocaleString(); } catch { return v; }
}
